import React, { useCallback, useRef } from 'react'
import { ArrowCaret } from '@fs/zion-icon'
import { ListItem } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import {
  getPersonName,
  getPrimaryRelationshipText,
  getRelationshipToPrimaryPersonDisplayLabel,
  getRelationshipToPrimaryPersonText,
  useSlsStateContext,
  useSlsHighlightsContext,
} from '@fs/zion-sls'
import { useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import { usePersonAttachmentData } from '../../hooks/usePersonAttachmentData'
import {
  getAttachmentStatusText,
  getIconByAttachmentStatus,
  getIconBackgroundColor,
  getIconTestId,
} from './PersonDisplayV2.utils'

export default function PersonDisplayV2({ person, record, isSelected, onPersonSelect = () => {} }) {
  const [t] = useTranslation()
  const zionSlsState = useSlsStateContext()
  const r2State = useR2StateContext()
  const { hoverHighlights, unHover } = useSlsHighlightsContext()
  const listItemRef = useRef()
  const r2SelectedPersonIdRef = useRef()

  if (isSelected && r2SelectedPersonIdRef.current !== r2State.selectedPersonId && listItemRef.current) {
    r2SelectedPersonIdRef.current = r2State.selectedPersonId
  }

  const { attachment, match } = usePersonAttachmentData(person?.id)

  const personName = getPersonName({ person })
  const primaryRelationshipLabel = `${getRelationshipToPrimaryPersonDisplayLabel(person, zionSlsState.types, record)}`
  const relationshipText = `${getRelationshipToPrimaryPersonText(person, zionSlsState.types, record, t)}`
  const primaryRelationshipText = getPrimaryRelationshipText(primaryRelationshipLabel, relationshipText)

  // Get attachment status text for secondary line
  const attachmentStatusText = getAttachmentStatusText(attachment, match, t)

  // Always show icon based on attachment status
  const IconComponent = getIconByAttachmentStatus(attachment, match)
  const iconBackground = getIconBackgroundColor(attachment, match)
  const iconTestId = getIconTestId(attachment, match)
  const startElement = <ListItem.Icon Icon={IconComponent} background={iconBackground} data-testid={iconTestId} />

  const handleClick = useCallback(() => {
    onPersonSelect(person?.id, record?.id)
  }, [onPersonSelect, person?.id, record?.id])

  const handleMouseEnter = useCallback(() => {
    hoverHighlights({ elementIds: [person?.id] })
  }, [hoverHighlights, person?.id])

  const handleMouseLeave = useCallback(() => {
    unHover()
  }, [unHover])

  const endelementCaretButtonTitle = `${t('person-display.button.person-select', 'Click to view')} ${personName}`

  const endElement = (
    <ListItem.IconButton
      Icon={ArrowCaret}
      color="gray"
      title={endelementCaretButtonTitle}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  )

  if (!person) return null

  return (
    <ListItem
      ref={listItemRef}
      data-testid={personName}
      key={person?.id}
      overline={primaryRelationshipText}
      primaryText={personName}
      secondaryText={attachmentStatusText}
      emphasized={!!person?.primary}
      startElement={startElement}
      endElement={endElement}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      selected={isSelected}
    />
  )
}
